import { useCallback, useEffect, useRef } from 'react';
import type { UserRole } from '@repo/shared-types';
import { captureOnboardingEvent, ONBOARDING_ANALYTICS, type OnboardingFlowKind } from '../../analytics/onboardingFunnel';

export type OnboardingAbandonReason =
  | 'close_button'
  | 'route_change'
  | 'refresh_or_tab_close'
  | 'modal_dismiss'
  | 'visibility_hidden';

type Params = {
  enabled: boolean;
  role: UserRole;
  flow: OnboardingFlowKind;
  getStepId: () => string;
  getStepIndex: () => number;
  /** When true, abandonment must not fire (completed or never started meaningfully). */
  isFlowComplete: () => boolean;
};

/**
 * Deduped onboarding_abandoned capture for lifecycle exits (pagehide/beforeunload) and explicit closes.
 * Call `emitAbandon` from the wizard close handler with `close_button` (or `modal_dismiss` from parent chrome).
 */
export function useOnboardingAbandonTelemetry(params: Params) {
  const sentRef = useRef(false);
  const paramsRef = useRef(params);

  useEffect(() => {
    paramsRef.current = params;
  }, [params]);

  useEffect(() => {
    if (params.enabled) sentRef.current = false;
  }, [params.enabled]);

  const emitAbandon = useCallback((termination_reason: OnboardingAbandonReason) => {
    const p = paramsRef.current;
    if (!p.enabled || p.isFlowComplete()) return;
    if (sentRef.current) return;
    sentRef.current = true;
    captureOnboardingEvent(ONBOARDING_ANALYTICS.ONBOARDING_ABANDONED, {
      role: p.role,
      flow: p.flow,
      stepId: p.getStepId() || 'unknown',
      stepIndex: p.getStepIndex(),
      extra: { termination_reason },
    });
  }, []);

  useEffect(() => {
    if (!params.enabled) return;
    const onPageHide = () => emitAbandon('refresh_or_tab_close');
    const onBeforeUnload = () => emitAbandon('refresh_or_tab_close');
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') emitAbandon('visibility_hidden');
    };
    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('beforeunload', onBeforeUnload);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('pagehide', onPageHide);
      window.removeEventListener('beforeunload', onBeforeUnload);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [params.enabled, emitAbandon]);

  return { emitAbandon };
}
